import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { DnsRecord } from '@/api/hooks/use-domains'

interface VerificationCardProps {
  records: DnsRecord[]
  isFetching: boolean
  dataUpdatedAt: number
  onCheckNow: () => void
}

function getDkimLabel(record: DnsRecord): string | null {
  if (record.purpose.toLowerCase() !== 'dkim') return null
  if (record.name.includes('oe1rsa._domainkey')) return 'DKIM (RSA)'
  if (record.name.includes('oe1._domainkey')) return 'DKIM (Ed25519)'
  return null
}

function formatElapsed(ms: number): string {
  if (ms < 1000) return 'just now'
  const seconds = Math.floor(ms / 1000)
  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.floor(seconds / 60)
  return `${minutes}m ago`
}

export function VerificationCard({
  records,
  isFetching,
  dataUpdatedAt,
  onCheckNow,
}: VerificationCardProps): React.JSX.Element {
  const [now, setNow] = useState(Date.now())

  // Tick every 5 seconds to update "last checked" display
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 5_000)
    return () => clearInterval(interval)
  }, [])

  const verified = records.filter((r) => r.status === 'verified')
  const total = records.length
  const elapsed = dataUpdatedAt > 0 ? now - dataUpdatedAt : 0
  const lastCheckedText = dataUpdatedAt > 0 ? formatElapsed(elapsed) : 'not yet'

  return (
    <div className="rounded-xl border border-surface-3 bg-surface-1 p-5">
      {/* Header with polling status */}
      <div className="flex items-center gap-2 mb-4">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-text-tertiary" />
        <span className="text-xs text-text-tertiary">Checking every 30s — don't refresh</span>
      </div>

      {/* Record status list */}
      <div className="flex flex-col gap-2">
        {records.map((record) => {
          const isRecordVerified = record.status === 'verified'
          const label = getDkimLabel(record) ?? record.purpose.toUpperCase()
          return (
            <div
              key={`${record.type}-${record.name}`}
              className="flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <span className={isRecordVerified ? 'text-green-600' : 'text-text-tertiary'}>
                  {isRecordVerified ? '\u2713' : '\u25CB'}
                </span>
                <span className="text-sm text-text-primary">{label}</span>
              </div>
              <span
                className={`text-xs ${isRecordVerified ? 'text-green-600' : 'text-text-tertiary'}`}
              >
                {isRecordVerified ? 'found' : 'waiting...'}
              </span>
            </div>
          )
        })}
      </div>

      {/* Progress counter */}
      <div className="mt-4 pt-4 border-t border-surface-3">
        <p className="text-xs font-medium text-text-secondary">
          {verified.length} of {total} records verified
        </p>
      </div>

      {/* DNS propagation guidance */}
      {verified.length < total && (
        <p className="mt-3 text-xs text-text-tertiary">
          DNS changes can take up to 48 hours to propagate, but most providers update within 5–30
          minutes. Check your records externally:{' '}
          <a
            href="https://dnschecker.org"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-600 hover:underline"
          >
            dnschecker.org
          </a>{' '}
          ·{' '}
          <a
            href="https://mxtoolbox.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-600 hover:underline"
          >
            mxtoolbox.com
          </a>
        </p>
      )}

      {/* Check now + last checked */}
      <div className="mt-3 flex items-center gap-3">
        <button
          type="button"
          onClick={onCheckNow}
          disabled={isFetching}
          className="inline-flex items-center gap-1.5 rounded-lg border border-surface-3 bg-surface-0
            px-3 py-1.5 text-xs font-medium text-text-primary hover:bg-surface-1
            disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isFetching && <Loader2 className="h-3 w-3 animate-spin" />}
          Check now
        </button>
        <span className="text-xs text-text-tertiary">Last checked: {lastCheckedText}</span>
      </div>
    </div>
  )
}
